import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const AmountRangePopover = ({ isOpen, onClose, onApply, position }) => {
  const { t } = useTranslation();
  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(100000);
  const [leftValue, setLeftValue] = useState(20000);
  const [rightValue, setRightValue] = useState(63595);
  const [isDragging, setIsDragging] = useState(null);
  const sliderRef = useRef(null);
  const popoverRef = useRef(null);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleMouseDown = (handle) => {
    setIsDragging(handle);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
    if (!clientX) return;

    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const value = Math.round((percentage / 100) * (maxValue - minValue) + minValue);

    if (isDragging === 'left') {
      const newValue = Math.min(value, rightValue - 1000);
      setLeftValue(newValue);
    } else if (isDragging === 'right') {
      const newValue = Math.max(value, leftValue + 1000);
      setRightValue(newValue);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(null);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleMouseMove, { passive: false });
      document.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, leftValue, rightValue]);

  const leftPercentage = ((leftValue - minValue) / (maxValue - minValue)) * 100;
  const rightPercentage = ((rightValue - minValue) / (maxValue - minValue)) * 100;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value).replace('$', '$ ');
  };

  const handleFromChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    setLeftValue(Math.min(value, rightValue - 1000));
  };

  const handleToChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    setRightValue(Math.max(value, leftValue + 1000));
  };

  const handleSearch = () => {
    onApply({ min: leftValue, max: rightValue });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={popoverRef}
      className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4 sm:p-6 w-72 sm:w-80 max-w-[calc(100vw-2rem)]"
      style={{
        top: position?.top || 0,
        left: Math.min(position?.left || 0, window.innerWidth - 320), // Prevent overflow
      }}
    >
      {/* Range Slider */}
      <div className="mb-6">
        <div className="relative mb-4">
          {/* Min/Max labels */}
          <div className="flex justify-between mb-2">
            <div className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
              {formatCurrency(minValue)}
            </div>
            <div className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
              {formatCurrency(maxValue)}
            </div>
          </div>

          {/* Slider track */}
          <div
            ref={sliderRef}
            className="relative h-3 sm:h-2 bg-gray-200 rounded-full cursor-pointer touch-none"
            onMouseMove={handleMouseMove}
            onTouchMove={handleMouseMove}
          >
            {/* Selected range */}
            <div
              className="absolute h-3 sm:h-2 bg-blue-500 rounded-full"
              style={{
                left: `${leftPercentage}%`,
                width: `${rightPercentage - leftPercentage}%`,
              }}
            />

            {/* Left handle */}
            <div
              className="absolute w-6 h-6 sm:w-4 sm:h-4 bg-white border-2 border-blue-500 rounded-full cursor-pointer transform -translate-y-1.5 sm:-translate-y-1 touch-none"
              style={{ left: `calc(${leftPercentage}% - 12px)` }}
              onMouseDown={() => handleMouseDown('left')}
              onTouchStart={() => handleMouseDown('left')}
            >
              {/* Value label above left handle */}
              <div className="absolute -top-10 sm:-top-8 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-2 py-1 rounded text-xs whitespace-nowrap">
                {formatCurrency(leftValue)}
              </div>
            </div>

            {/* Right handle */}
            <div
              className="absolute w-6 h-6 sm:w-4 sm:h-4 bg-white border-2 border-blue-500 rounded-full cursor-pointer transform -translate-y-1.5 sm:-translate-y-1 touch-none"
              style={{ left: `calc(${rightPercentage}% - 12px)` }}
              onMouseDown={() => handleMouseDown('right')}
              onTouchStart={() => handleMouseDown('right')}
            >
              {/* Value label above right handle */}
              <div className="absolute -top-10 sm:-top-8 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-2 py-1 rounded text-xs whitespace-nowrap">
                {formatCurrency(rightValue)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Input Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
            {t('transactions.amountRange.from')}
          </label>
          <input
            type="number"
            value={leftValue}
            onChange={handleFromChange}
            className="w-full px-3 py-2 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
            style={{ backgroundColor: '#F6F7F8' }}
            inputMode="numeric"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
            {t('transactions.amountRange.to')}
          </label>
          <input
            type="number"
            value={rightValue}
            onChange={handleToChange}
            className="w-full px-3 py-2 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
            style={{ backgroundColor: '#F6F7F8' }}
            inputMode="numeric"
          />
        </div>
      </div>

      {/* Search Button */}
      <div className="flex justify-center">
        <button
          onClick={handleSearch}
          className="bg-blue-600 text-white px-6 py-2 sm:py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors w-full text-base sm:text-sm"
          style={{
            background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)",
            borderRadius: "10px",
          }}
        >
          {t('transactions.amountRange.search')}
        </button>

      </div>
    </div>
  );
};

export default AmountRangePopover;
